import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import MobileNavigation from "../../components/ui/MobileNavigation";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // ✅ Correct import

const ExpensePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Redux state
  const { sale, totalExpense, expense } = useSelector((state) => state.sale);

  // Totals
  const totalSales = sale?.reduce((sum, t) => sum + t.amount, 0) || 0;
  const totalExpenses = totalExpense || 0;

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      navigate("/whats-app-authentication");
    }
  }, [navigate]);

  const handleSidebarToggle = () => setSidebarCollapsed(!sidebarCollapsed);
  const handleMobileNavToggle = () => setMobileNavOpen(!mobileNavOpen);

  // Filtered Expense Data
  const filteredExpenses = expense?.filter((exp) => {
    const expDate = new Date(exp.date);
    const fromDate = startDate ? new Date(startDate) : null;
    const toDate = endDate ? new Date(endDate) : null;

    const matchDate =
      (!fromDate || expDate >= fromDate) && (!toDate || expDate <= toDate);

    const matchEmployee =
      !selectedEmployee || exp.employeeId?._id === selectedEmployee;

    return matchDate && matchEmployee;
  });

  // Unique Employees
  const uniqueEmployees = [
    ...new Map(
      expense?.map((exp) => [exp.employeeId?._id, exp.employeeId])
    ).values(),
  ];

  // ✅ Export as PDF (Fixed)
  const handleExportPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Expense Report", 14, 20);

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(
      `Total Sales: ₹${totalSales.toFixed(2)} | Total Expenses: ₹${totalExpenses.toFixed(
        2
      )}`,
      14,
      30
    );

    const tableColumn = ["#", "Description", "Amount", "Date", "Employee"];
    const tableRows = [];

    filteredExpenses.forEach((exp, index) => {
      tableRows.push([
        index + 1,
        exp.name,
        `₹${exp.amount.toFixed(2)}`,
        new Date(exp.date).toLocaleDateString(),
        exp.employeeId?.name || "N/A",
      ]);
    });

    // ✅ Use imported autoTable plugin
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      theme: "striped",
      styles: { fontSize: 10 },
    });

    doc.save("Expense_Report.pdf");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon
            name="Loader2"
            size={32}
            className="animate-spin mx-auto mb-4 text-primary"
          />
          <p className="text-muted-foreground">Loading expense page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        onSidebarToggle={handleMobileNavToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

      <Sidebar
        user={user}
        collapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <MobileNavigation
        user={user}
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      <main
        className={`pt-16 transition-all duration-200 ${
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-60"
        }`}
      >
        <div className="p-6">
          <Breadcrumb user={user} />

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Expense Management
              </h1>
              <p className="text-muted-foreground">
                Track and manage your expenses along with total sales.
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                iconSize={16}
                onClick={handleExportPDF}
              >
                Export
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h3 className="text-sm font-medium text-muted-foreground">
                Total Sales
              </h3>
              <p className="mt-2 text-2xl font-bold text-foreground">
                ₹{totalSales.toFixed(2)}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h3 className="text-sm font-medium text-muted-foreground">
                Total Expenses
              </h3>
              <p className="mt-2 text-2xl font-bold text-foreground">
                ₹{totalExpenses.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Expense Table with Filters */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
              <h2 className="text-xl font-semibold text-foreground">
                Recent Expenses
              </h2>

              {/* Filters */}
              <div className="flex flex-wrap gap-3">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-border rounded px-3 py-2 text-sm"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-border rounded px-3 py-2 text-sm"
                />
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="border border-border rounded px-3 py-2 text-sm"
                >
                  <option value="">All Employees</option>
                  {uniqueEmployees.map((emp) => (
                    <option key={emp?._id} value={emp?._id}>
                      {emp?.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Employee</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses?.length > 0 ? (
                    filteredExpenses.map((exp, index) => (
                      <tr
                        key={exp._id}
                        className="border-b border-border hover:bg-muted/50 micro-animation"
                      >
                        <td className="px-4 py-3 font-medium text-foreground">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3">{exp.name}</td>
                        <td className="px-4 py-3">₹{exp.amount.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          {new Date(exp.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                            {exp.employeeId?.name}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="text-center text-muted-foreground py-4"
                      >
                        No expenses found for selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExpensePage;
